import type { MouseEvent } from 'react';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { Icon } from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { Tooltip } from '@/components/ui/tooltip';
import { SLOT_CARD_ACTION_ICON_SIZE, SLOT_CARD_ICON_TARGET_SIZE } from './slotCardStyles';

/**
 * The primary action on a morphing slot card: a plus that rotates into a cancel
 * ✕ while the card's form is open. Shared by the important-systems and service
 * category cards so the affordance stays identical across both.
 */
interface SlotActionButtonProps {
	ariaLabel: string;
	tooltipLabel: string;
	isCancel?: boolean;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

const SlotActionButton = (props: SlotActionButtonProps) => {
	const { ariaLabel, tooltipLabel, isCancel = false, onClick } = props;

	return (
		<Tooltip content={tooltipLabel} openDelay={300} closeDelay={100}>
			<OutlinedButton
				aria-label={ariaLabel}
				minW={SLOT_CARD_ICON_TARGET_SIZE}
				w={SLOT_CARD_ICON_TARGET_SIZE}
				h={SLOT_CARD_ICON_TARGET_SIZE}
				p={0}
				border="none"
				bg="transparent"
				color="fg.muted"
				_hover={{ bg: 'surface.subtle', color: 'brand.fg' }}
				size="xs"
				// Cancel closes a form whose first required field is focused. Suppressing the default
				// pointerdown focus shift keeps that field from blurring, so onTouched validation
				// doesn't flash errors as the card collapses (matches the form's Cancel button).
				onPointerDown={isCancel ? (event) => event.preventDefault() : undefined}
				onClick={onClick}
			>
				<Icon
					as={FiPlus}
					boxSize={SLOT_CARD_ACTION_ICON_SIZE}
					transform={isCancel ? 'rotate(45deg)' : 'rotate(0deg)'}
					transition="transform 160ms ease"
				/>
			</OutlinedButton>
		</Tooltip>
	);
};

export default SlotActionButton;
